import React, { useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { TreeView, TreeViewRef } from 'devextreme-react/tree-view';
import * as events from 'devextreme/events';
import { navigation } from '../../configs/app-navigation';
import ServerSideVerticalNavItems from '../../configs/app-navigation2';
import { useNavigation } from '../../contexts/navigation';
import { useScreenSize } from '../../utils/media-query';
import CircularProgress from '@mui/material/CircularProgress';
import './SideNavigationMenu.scss';
import type { SideNavigationMenuProps } from '../../types';
import { ThemeContext } from '../../theme';

export default function SideNavigationMenu(props: React.PropsWithChildren<SideNavigationMenuProps>) {
  const {
    children,
    selectedItemChanged,
    openMenu,
    compactMode,
    onMenuReady
  } = props;

  const { menuItems: verticalMenuItems } = ServerSideVerticalNavItems();

  const theme = useContext(ThemeContext);
  const { isLarge } = useScreenSize();
  const { isMedium } = useScreenSize();

  const items = useMemo(() => {
    return verticalMenuItems.map((item: any) => ({
      ...item,
      expanded: isMedium,
      path: item.path && !item.path.startsWith('/') ? `/${item.path}` : item.path
    }));
  }, [verticalMenuItems, isMedium]);

  const { navigationData: { currentPath } } = useNavigation();

  const treeViewRef = useRef<TreeViewRef>(null);
  const wrapperRef = useRef<HTMLDivElement>();
  const getWrapperRef = useCallback((element: HTMLDivElement) => {
    const prevElement = wrapperRef.current;
    if (prevElement) {
      events.off(prevElement, 'dxclick');
    }

    wrapperRef.current = element;
    events.on(element, 'dxclick', (e: React.PointerEvent) => {
      openMenu(e);
    });
  }, [openMenu]);

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance();
    if (!treeView) return;

    if (currentPath !== undefined) {
      treeView.selectItem(currentPath);
      treeView.expandItem(currentPath);
    }

    if (compactMode) {
      treeView.collapseAll();
    }
  }, [currentPath, compactMode, verticalMenuItems]);

  // 🔄 Tampilkan loading spinner jika menu belum siap
  if (!verticalMenuItems || verticalMenuItems.length === 0) {
    return (
      <div
        className={`dx-swatch-additional${theme?.isDark() ? '-dark' : ''} side-navigation-menu`}
        ref={getWrapperRef}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
      >
        <CircularProgress color='primary' />
      </div>
    );
  }

  return (
    <div
      className={`dx-swatch-additional${theme?.isDark() ? '-dark' : ''} side-navigation-menu`}
      ref={getWrapperRef}
    >
      {children}
      <div className={'menu-container'}>
        <TreeView
          ref={treeViewRef}
          items={items}
          keyExpr={'path'}
          selectionMode={'single'}
          focusStateEnabled={false}
          expandEvent={'click'}
          onItemClick={selectedItemChanged}
          onContentReady={onMenuReady}
          width={'100%'}
        />
      </div>
    </div>
  );
}
